import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { AlertMessageService } from '@sigepe/sigepe-template';
import { FeriasBaseComponent } from '../../ferias-base.component';
import { ExercicioSelecionado, ExerciciosService, FeriasEspeciaisAdministrativas } from 'src/app/services/exercicios.service';
import { Message } from 'src/app/components/shared/message';

type CampoFormulario = 'numeroProcesso' | 'quantidadeDias' | 'justificativa';

@Component({
    selector: 'app-ferias-especiais-administrativas',
    templateUrl: './ferias-especiais-administrativas.component.html',
    styleUrls: ['./ferias-especiais-administrativas.component.scss']
})
export class FeriasEspeciaisAdministrativasComponent extends FeriasBaseComponent implements OnInit {
    readonly formulario: FormGroup;

    exercicioSelecionado!: ExercicioSelecionado;
    submetido = false;
    houvePreenchimento = false;

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly exerciciosService: ExerciciosService,
        alertaMsgService: AlertMessageService,
        private readonly confirmationService: ConfirmationService,
        private readonly router: Router
    ) {
        super(alertaMsgService);
        this.formulario = this.formBuilder.group({
            numeroProcesso: ['', [Validators.required, Validators.pattern(/^\d{5}\.\d{6}\/\d{4}-\d{2}$/)]],
            quantidadeDias: ['', [Validators.required, Validators.pattern(/^(?:[1-9]|[1-3][0-9]|4[0-5])$/)]],

            justificativa: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(300)]]
        });
    }

    ngOnInit(): void {
        this.exercicioSelecionado = this.router.getCurrentNavigation()?.extras?.state?.['data'] ?? history.state?.data ?? JSON.parse(sessionStorage.getItem('consultaGerencialExercicioSelecionado') ?? 'null');

        if (this.exercicioSelecionado) {
            this.formulario.patchValue(
                {
                    quantidadeDias: this.exercicioSelecionado.saldoDias
                },
                { emitEvent: false }
            );
        }
    }

    campoInvalido(nome: CampoFormulario): boolean {
        const campo = this.formulario.controls[nome];
        return campo.invalid && (campo.touched || this.submetido);
    }

    campoObrigatorioNaoInformado(nome: CampoFormulario): boolean {
        if (!this.campoInvalido(nome)) {
            return false;
        }

        const valor = this.formulario.controls[nome].value;
        switch (nome) {
            case 'numeroProcesso':
            case 'justificativa':
                return typeof valor !== 'string' || valor.trim().length === 0;
            case 'quantidadeDias':
                return valor === '' || valor === null || valor === undefined;
        }
    }

    private get existeCampoObrigatorioNaoInformado(): boolean {
        return this.campoObrigatorioNaoInformado('numeroProcesso') || this.campoObrigatorioNaoInformado('quantidadeDias') || this.campoObrigatorioNaoInformado('justificativa');
    }

    registrarAlteracao(): void {
        this.houvePreenchimento = true;
    }

    validarInputQuantidadeDias(event: Event): void {
        const input = event.target as HTMLInputElement;

        const valor = input.value.replace(/\D/g, '').slice(0, 2);

        input.value = valor;

        this.formulario.patchValue(
            {
                quantidadeDias: valor
            },
            {
                emitEvent: false
            }
        );

        this.registrarAlteracao();
    }

    concluir(): void {
        this.submetido = true;
        this.clearMessages();

        if (this.formulario.invalid || !this.exercicioSelecionado) {
            this.formulario.markAllAsTouched();
            if (this.existeCampoObrigatorioNaoInformado) {
                this.getAlertaMsgService().error(Message.ERRO_CAMPOS_OBRIGATORIOS_NAO_PREENCHIDOS);
                this.rolarParaTopo();
            }
            return;
        }

        const numeroProcesso = this.formulario.controls.numeroProcesso.value;
        const quantidadeDias = this.formulario.controls.quantidadeDias.value;
        const justificativa = this.formulario.controls.justificativa.value;
        if (numeroProcesso === null || numeroProcesso === undefined || quantidadeDias === null || quantidadeDias === undefined || justificativa === null || justificativa === undefined) {
            this.formulario.markAllAsTouched();
            return;
        }

        const dados: FeriasEspeciaisAdministrativas = {
            numeroProcesso,
            quantidadeDias,
            justificativa: justificativa.trim()
        };

        this.exerciciosService.feriasEspeciaisAdministrativas(this.exercicioSelecionado.idFerias, dados).subscribe({
            next: () => {
                this.getAlertaMsgService().setAwatingSuccessMessage('Férias Especiais Administrativas registradas com sucesso.');
                this.router.navigate(['/secure/gerenciar-ferias/consultar-exercicios']);
            },
            error: () => {
                this.getAlertaMsgService().setAwatingErrorMessage('Não foi possível concluir as Férias Especiais Administrativas.');
            }
        });
    }

    confirmarVoltar() {
        if (this.houvePreenchimento) {
            this.confirmationService.confirm({
                key: 'confirmPadrao',
                header: 'Confirmação',
                message: Message.CONFIRMACAO_ABANDONAR_TELA_COM_PERDA_DE_DADOS,
                accept: async () => {
                    this.voltar();
                }
            });
        } else {
            this.voltar();
        }
    }

    voltar(): void {
        this.router.navigate(['/secure/gerenciar-ferias/consultar-exercicios']);
    }
}
